import { useState, useEffect } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CurrencyCircleDollar, Plus, Trash, Users, TrendUp, Info } from "@phosphor-icons/react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fc = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);

export default function EventPricing({ event, open, onOpenChange }) {
  const [method, setMethod] = useState("profit_margin");
  const [value, setValue] = useState("40");
  const [numGuests, setNumGuests] = useState("50");
  const [extraCosts, setExtraCosts] = useState([
    { description: "Mão de obra (garçom/ajudantes)", value: "" },
    { description: "Transporte/Logística", value: "" },
  ]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && event) {
      setResult(null);
    }
  }, [open, event]);

  const addExtraCost = () => {
    setExtraCosts([...extraCosts, { description: "", value: "" }]);
  };

  const removeExtraCost = (idx) => {
    setExtraCosts(extraCosts.filter((_, i) => i !== idx));
  };

  const updateExtraCost = (idx, field, val) => {
    const updated = [...extraCosts];
    updated[idx] = { ...updated[idx], [field]: val };
    setExtraCosts(updated);
  };

  const handleCalculate = async () => {
    const numVal = parseFloat(value);
    if (!numVal || numVal <= 0) { toast.error("Informe um valor válido"); return; }
    if (method === "profit_margin" && numVal >= 100) {
      toast.error("Margem de lucro deve ser menor que 100%");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/events/${event.id}/pricing`, {
        pricing_method: method,
        value: numVal,
        num_guests: parseInt(numGuests) || 1,
        extra_costs: extraCosts
          .filter((ec) => ec.description && ec.value)
          .map((ec) => ({ description: ec.description, value: parseFloat(ec.value) || 0 })),
      });
      setResult(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao calcular");
    } finally {
      setLoading(false);
    }
  };

  // Reactive calc on value change
  useEffect(() => {
    if (result && value && parseFloat(value) > 0) {
      const timer = setTimeout(() => handleCalculate(), 600);
      return () => clearTimeout(timer);
    }
  }, [value, method, numGuests]);

  if (!event) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold flex items-center gap-2">
            <CurrencyCircleDollar size={24} weight="fill" />
            Precificação: {event.name}
          </DialogTitle>
          <DialogDescription className="sr-only">Precificação do evento</DialogDescription>
        </DialogHeader>

        <div className="space-y-5 mt-2">
          {/* Guests */}
          <div className="flex items-center gap-3">
            <Users size={18} className="text-zinc-500" />
            <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700 shrink-0">Convidados / PAX</Label>
            <Input
              type="number" min="1" value={numGuests}
              onChange={(e) => setNumGuests(e.target.value)}
              className="w-24 h-8 text-sm" data-testid="event-guests-input"
            />
          </div>

          {/* Extra Costs */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700">Custos Extras do Evento</Label>
              <Button variant="ghost" size="sm" onClick={addExtraCost} className="h-7 text-xs">
                <Plus size={14} className="mr-1" /> Adicionar
              </Button>
            </div>
            <div className="space-y-2">
              {extraCosts.map((ec, idx) => (
                <div key={`ec-${idx}`} className="flex gap-2 items-center">
                  <Input
                    value={ec.description}
                    onChange={(e) => updateExtraCost(idx, "description", e.target.value)}
                    placeholder="Descrição (ex: Garçom)"
                    className="flex-1 h-8 text-sm"
                  />
                  <div className="relative w-28">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-zinc-400">R$</span>
                    <Input
                      type="number" step="0.01"
                      value={ec.value}
                      onChange={(e) => updateExtraCost(idx, "value", e.target.value)}
                      placeholder="0,00"
                      className="h-8 text-sm pl-7"
                    />
                  </div>
                  <button onClick={() => removeExtraCost(idx)} className="text-zinc-400 hover:text-red-500 p-1">
                    <Trash size={14} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Pricing Method */}
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-700 mb-2 block">Método de Precificação</Label>
            <Tabs value={method} onValueChange={(v) => { setMethod(v); setResult(null); }}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="profit_margin">Margem de Lucro</TabsTrigger>
                <TabsTrigger value="markup">Markup</TabsTrigger>
              </TabsList>
              <TabsContent value="profit_margin" className="mt-3">
                <Input type="number" step="0.1" max="99.99" value={value}
                  onChange={(e) => { if (parseFloat(e.target.value) >= 100) return; setValue(e.target.value); }}
                  placeholder="Ex: 40" className="text-lg font-mono" data-testid="event-margin-input" />
                <p className="text-xs text-zinc-500 mt-1">% sobre o preço de venda. Deve ser menor que 100%.</p>
              </TabsContent>
              <TabsContent value="markup" className="mt-3">
                <Input type="number" step="0.1" value={value}
                  onChange={(e) => setValue(e.target.value)}
                  placeholder="Ex: 100" className="text-lg font-mono" data-testid="event-markup-input" />
                <p className="text-xs text-zinc-500 mt-1">% sobre o custo total. Ex: 100% = preço é o dobro do custo.</p>
              </TabsContent>
            </Tabs>
          </div>

          <Button onClick={handleCalculate} disabled={loading} className="w-full bg-zinc-950 text-white hover:bg-zinc-800" data-testid="calc-event-pricing-btn">
            <TrendUp size={18} className="mr-2" /> {loading ? "Calculando..." : "Calcular Preço do Evento"}
          </Button>

          {/* Results */}
          {result && (
            <div className="space-y-4 pt-2">
              {/* Cost Breakdown */}
              <div className="p-4 bg-zinc-50 border border-zinc-200 rounded-lg">
                <p className="text-xs font-bold uppercase tracking-wider text-zinc-600 mb-2">Resumo de Custos</p>
                {result.sheets_breakdown.map((sb) => (
                  <div key={sb.name} className="flex justify-between text-sm py-0.5">
                    <span className="text-zinc-700">{sb.name} (×{sb.quantity})</span>
                    <span className="font-mono text-zinc-900">{fc(sb.total_cost)}</span>
                  </div>
                ))}
                <div className="border-t border-zinc-200 mt-2 pt-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-zinc-600">Custo Insumos (CMV)</span>
                    <span className="font-mono font-semibold">{fc(result.ingredient_cost)}</span>
                  </div>
                </div>
                {result.extra_costs?.length > 0 && (
                  <>
                    {result.extra_costs.map((ec, i) => (
                      <div key={`r-ec-${i}`} className="flex justify-between text-sm py-0.5">
                        <span className="text-zinc-600">{ec.description}</span>
                        <span className="font-mono">{fc(ec.value)}</span>
                      </div>
                    ))}
                    <div className="flex justify-between text-sm">
                      <span className="text-zinc-600">Total Extras</span>
                      <span className="font-mono">{fc(result.extra_costs_total)}</span>
                    </div>
                  </>
                )}
                <div className="border-t border-zinc-300 mt-2 pt-2 flex justify-between text-sm font-bold">
                  <span className="text-zinc-900">CUSTO TOTAL OPERACIONAL</span>
                  <span className="font-mono text-zinc-900">{fc(result.total_cost)}</span>
                </div>
              </div>

              {/* Final Price */}
              <div className="p-5 bg-emerald-50 border-2 border-emerald-300 rounded-lg text-center">
                <p className="text-xs font-bold uppercase tracking-wider text-emerald-700 mb-1">Preço de Venda do Evento</p>
                <p className="text-4xl font-mono font-bold text-emerald-800" data-testid="event-selling-price">{fc(result.selling_price)}</p>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-center">
                  <p className="text-[10px] font-bold uppercase text-blue-600">Por Convidado</p>
                  <p className="text-xl font-mono font-bold text-blue-800 mt-1" data-testid="event-price-per-guest">{fc(result.price_per_guest)}</p>
                </div>
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-center">
                  <p className="text-[10px] font-bold uppercase text-green-600">Lucro Líquido</p>
                  <p className="text-xl font-mono font-bold text-green-800 mt-1" data-testid="event-profit">{fc(result.profit)}</p>
                </div>
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-center">
                  <p className="text-[10px] font-bold uppercase text-amber-600">Margem Real</p>
                  <p className="text-xl font-mono font-bold text-amber-800 mt-1">{result.margin_percentage.toFixed(1)}%</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
